import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatEuro, formatNumber, formatPercent, formatDateFr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DoorOpen, TrendingUp, Coins, Landmark, ShieldCheck, PenLine, Save, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  BUILDING_TYPE_LABELS, BUILDING_STATUS_LABELS, OWNERSHIP_REGIME_LABELS,
} from "@/constants/patrimoine";
import StatusBadge from "@/components/common/StatusBadge";

const CHARGE_KEYS = [
  ["property_tax", "Taxe foncière"], ["insurance_pno", "Assurance PNO"],
  ["water", "Eau"], ["electricity_common", "Électricité communs"],
  ["gas", "Gaz"], ["maintenance", "Entretien"], ["other", "Autres"],
];

export default function BuildingSynthesisTab({ building, onSaved }) {
  const { activeRole } = useAuth();
  const canEdit = ["owner", "admin", "manager"].includes(activeRole);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...building, common_charges: { ...(building.common_charges || {}) } });
  const [lots, setLots] = useState([]);
  const [completeness, setCompleteness] = useState(null);
  const [financings, setFinancings] = useState([]);

  const load = async () => {
    const [l, c, f] = await Promise.all([
      api.get(`/lots?building_id=${building.id}`),
      api.get(`/buildings/${building.id}/completeness`),
      api.get(`/financings?building_id=${building.id}`),
    ]);
    setLots(l.data.items || []); setCompleteness(c.data); setFinancings(f.data.items || []);
  };
  useEffect(() => {
    setForm({ ...building, common_charges: { ...(building.common_charges || {}) } });
    load();
    // eslint-disable-next-line
  }, [building.id]);

  const save = async () => {
    try {
      const payload = { ...form };
      ["acquisition_price", "notary_fees", "agency_fees", "initial_works",
       "current_value", "total_surface_m2", "latitude", "longitude"].forEach((k) => {
        if (payload[k] === "" || payload[k] == null) payload[k] = null;
        else payload[k] = parseFloat(payload[k]);
      });
      if (payload.year_built) payload.year_built = parseInt(payload.year_built);
      if (payload.common_charges) {
        Object.keys(payload.common_charges).forEach((k) => {
          const v = payload.common_charges[k];
          payload.common_charges[k] = v === "" || v == null ? null : parseFloat(v);
        });
      }
      delete payload.id; delete payload.organization_id;
      delete payload.created_at; delete payload.updated_at;
      await api.patch(`/buildings/${building.id}`, payload);
      toast.success("Bâtiment mis à jour"); setEditing(false); onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Enregistrement impossible"); }
  };

  const monthlyRent = lots.reduce((a, l) => a + Number(l.target_rent || l.monthly_rent || 0), 0);
  const occupied = lots.filter((l) => l.occupancy_status === "occupied" || l.is_occupied).length;
  const rate = lots.length ? Math.round((occupied / lots.length) * 1000) / 10 : 0;
  const totalCharges = Object.values(building.common_charges || {}).reduce((a, v) => a + Number(v || 0), 0);

  if (editing) {
    return (
      <div className="space-y-6">
        <div className="card-elevated p-6">
          <div className="text-overline mb-4">Identité & acquisition</div>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Nom interne" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
            <div>
              <Label>Type</Label>
              <Select value={form.building_type} onValueChange={(v) => setForm({ ...form, building_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(BUILDING_TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Field label="Adresse" value={form.address} onChange={(v) => setForm({ ...form, address: v })} span={2} />
            <Field label="Code postal" value={form.postal_code || ""} onChange={(v) => setForm({ ...form, postal_code: v })} />
            <Field label="Ville" value={form.city || ""} onChange={(v) => setForm({ ...form, city: v })} />
            <Field label="Référence cadastrale" value={form.cadastral_reference || ""} onChange={(v) => setForm({ ...form, cadastral_reference: v })} />
            <div>
              <Label>Statut</Label>
              <Select value={form.status || "in_operation"} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(BUILDING_STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Field label="Date d'acquisition" type="date" value={form.acquisition_date || ""} onChange={(v) => setForm({ ...form, acquisition_date: v })} />
            <Field label="Prix d'achat (€)" type="number" value={form.acquisition_price ?? ""} onChange={(v) => setForm({ ...form, acquisition_price: v })} />
            <Field label="Frais de notaire (€)" type="number" value={form.notary_fees ?? ""} onChange={(v) => setForm({ ...form, notary_fees: v })} />
            <Field label="Frais d'agence (€)" type="number" value={form.agency_fees ?? ""} onChange={(v) => setForm({ ...form, agency_fees: v })} />
            <Field label="Travaux initiaux (€)" type="number" value={form.initial_works ?? ""} onChange={(v) => setForm({ ...form, initial_works: v })} />
            <Field label="Valeur estimée actuelle (€)" type="number" value={form.current_value ?? ""} onChange={(v) => setForm({ ...form, current_value: v })} />
            <Field label="Surface totale (m²)" type="number" value={form.total_surface_m2 ?? ""} onChange={(v) => setForm({ ...form, total_surface_m2: v })} />
            <Field label="Année de construction" type="number" value={form.year_built ?? ""} onChange={(v) => setForm({ ...form, year_built: v })} />
            <div>
              <Label>Régime de propriété</Label>
              <Select value={form.ownership_regime || ""} onValueChange={(v) => setForm({ ...form, ownership_regime: v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{Object.entries(OWNERSHIP_REGIME_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Field label="Syndic / gestionnaire" value={form.syndic_manager || ""} onChange={(v) => setForm({ ...form, syndic_manager: v })} />
          </div>
        </div>

        <div className="card-elevated p-6">
          <div className="text-overline mb-4">Charges communes annuelles</div>
          <div className="grid gap-4 md:grid-cols-3">
            {CHARGE_KEYS.map(([key, label]) => (
              <Field key={key} label={`${label} (€)`} type="number"
                     value={form.common_charges?.[key] ?? ""}
                     onChange={(v) => setForm({ ...form, common_charges: { ...form.common_charges, [key]: v } })} />
            ))}
          </div>
        </div>

        <div className="card-elevated p-6">
          <div className="text-overline mb-4">Description</div>
          <Textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>

        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={() => setEditing(false)}>Annuler</Button>
          <Button onClick={save} className="btn-pill" data-testid="synthesis-save"><Save className="mr-2 h-4 w-4" /> Enregistrer</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header actions */}
      {canEdit && (
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => setEditing(true)} data-testid="synthesis-edit"><PenLine className="mr-2 h-4 w-4" /> Modifier la fiche</Button>
        </div>
      )}

      {/* KPI row */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MiniKpi icon={Landmark} label="Valeur estimée" value={formatEuro(building.current_value || building.acquisition_price)}
                 tone="accent" testid="synthesis-kpi-value" />
        <MiniKpi icon={DoorOpen} label="Lots" value={formatNumber(lots.length)} testid="synthesis-kpi-lots" />
        <MiniKpi icon={TrendingUp} label="Occupation" value={formatPercent(rate)} testid="synthesis-kpi-occupancy" />
        <MiniKpi icon={Coins} label="Loyers théoriques /mois" value={formatEuro(monthlyRent)} testid="synthesis-kpi-rent" />
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        {/* Identity + acquisition */}
        <div className="card-elevated p-6 lg:col-span-2">
          <div className="text-overline mb-3">Fiche d&apos;identité</div>
          <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm">
            <Info label="Type" value={BUILDING_TYPE_LABELS[building.building_type]} />
            <Info label="Statut" value={<StatusBadge tone="success">{BUILDING_STATUS_LABELS[building.status]}</StatusBadge>} />
            <Info label="Année de construction" value={building.year_built || "—"} />
            <Info label="Surface totale" value={building.total_surface_m2 ? `${formatNumber(building.total_surface_m2)} m²` : "—"} />
            <Info label="Régime" value={OWNERSHIP_REGIME_LABELS[building.ownership_regime] || "—"} />
            <Info label="Syndic / gestion" value={building.syndic_manager || "—"} />
            <Info label="Réf. cadastrale" value={building.cadastral_reference || "—"} />
            <Info label="Date acquisition" value={formatDateFr(building.acquisition_date)} />
            <Info label="Prix d'achat" value={formatEuro(building.acquisition_price)} />
            <Info label="Frais de notaire" value={formatEuro(building.notary_fees)} />
            <Info label="Frais d'agence" value={formatEuro(building.agency_fees)} />
            <Info label="Travaux initiaux" value={formatEuro(building.initial_works)} />
          </div>
          {building.description && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="text-overline mb-2">Description</div>
              <p className="text-sm text-muted-foreground leading-relaxed">{building.description}</p>
            </div>
          )}
        </div>

        {/* Common charges */}
        <div className="card-elevated p-6">
          <div className="text-overline mb-3">Charges communes annuelles</div>
          <ul className="space-y-2 text-sm">
            {CHARGE_KEYS.map(([key, label]) => (
              <li key={key} className="flex justify-between border-b border-border/60 pb-2 last:border-0">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-mono">{formatEuro(building.common_charges?.[key])}</span>
              </li>
            ))}
          </ul>
          <div className="mt-3 pt-3 border-t border-border flex justify-between items-center">
            <span className="text-xs uppercase tracking-widest text-muted-foreground">Total /an</span>
            <span className="font-serif text-lg">{formatEuro(totalCharges)}</span>
          </div>
        </div>
      </section>

      {/* Financings + Completeness */}
      <section className="grid gap-6 lg:grid-cols-3">
        <div className="card-elevated p-6 lg:col-span-2">
          <div className="flex items-center gap-2 text-overline mb-3">
            <ShieldCheck className="h-4 w-4" /> Financement connu
          </div>
          {financings.length === 0 && <div className="text-sm text-muted-foreground py-4">Aucun financement rattaché.</div>}
          {financings.map((f) => (
            <div key={f.id} className="rounded-xl border border-border p-4 flex items-start justify-between">
              <div>
                <div className="font-heading">{f.label}</div>
                <div className="text-xs text-muted-foreground">{f.bank} · Début {formatDateFr(f.start_date)}</div>
                <div className="mt-1 text-sm text-muted-foreground">Mensualité : <span className="font-mono text-foreground">{formatEuro(f.monthly_payment, true)}</span></div>
              </div>
              <div className="text-right">
                <div className="font-serif text-xl">{formatEuro(f.initial_amount)}</div>
                <div className="text-[10px] uppercase text-muted-foreground">Montant initial</div>
              </div>
            </div>
          ))}
        </div>

        <div className="card-elevated p-6">
          <div className="text-overline mb-1">Score de complétude</div>
          {completeness && (
            <>
              <div className="font-serif text-4xl">{completeness.pct} %</div>
              <div className="mt-3 h-2 rounded-full bg-secondary overflow-hidden">
                <div className={`h-full ${completeness.pct >= 80 ? "bg-emerald-600" : completeness.pct >= 50 ? "bg-amber-500" : "bg-red-500"}`}
                     style={{ width: `${completeness.pct}%` }} />
              </div>
              <div className="mt-5 space-y-2">
                {completeness.alerts.length === 0 && <div className="flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Fiche complète.</div>}
                {completeness.alerts.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-amber-800">
                    <AlertTriangle className="h-3.5 w-3.5 text-amber-500" /> {a}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function MiniKpi({ icon: Icon, label, value, tone, testid }) {
  return (
    <div data-testid={testid} className="card-elevated p-4 lg:p-5">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-widest text-accent">{label}</div>
        <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${tone === "accent" ? "bg-accent/10 text-accent" : "bg-primary/5 text-primary"}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="mt-2 font-heading text-xl lg:text-2xl">{value}</div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-foreground">{value ?? "—"}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", span, required }) {
  const spanClass = span === 2 ? "md:col-span-2" : "";
  return (
    <div className={spanClass}>
      <Label>{label}{required ? " *" : ""}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5" />
    </div>
  );
}
